import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth, formatApiError } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountRegister() {
  const { register, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, fullName);
      // Optionally apply referral code post-registration (best-effort)
      if (referralCode) {
        try {
          await axios.post(`${API}/loyalty/referral/apply`, { code: referralCode }, { withCredentials: true });
          toast.success(`Bienvenue ! Vous bénéficiez de -10% supplémentaires grâce au parrainage.`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Code de parrainage refusé");
        }
      } else {
        toast.success("Bienvenue chez MAISON DOUBA.");
      }
      navigate("/compte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Inscription impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-40 pb-24 px-6">
        <div className="max-w-md mx-auto" data-testid="account-register-page">
          <p className="label-caps text-[color:var(--gold)] text-[11px] tracking-[0.3em] mb-4 text-center">Rejoindre la Maison</p>
          <h1 className="font-serif text-5xl text-center mb-3">Créer un compte</h1>
          <p className="text-white/60 text-center text-sm mb-10">Suivez vos commandes, sauvegardez vos parfums favoris.</p>

          {/* Google sign-in — Emergent-managed OAuth */}
          <button
            data-testid="register-google"
            onClick={startGoogleLogin}
            type="button"
            className="w-full bg-white text-black py-3.5 label-caps text-xs tracking-[0.25em] flex items-center justify-center gap-3 hover:bg-white/90 transition-colors mb-6"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            S'inscrire avec Google
          </button>

          <div className="flex items-center gap-3 my-8">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label-caps text-[10px] text-white/40 tracking-[0.25em]">ou par email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Nom complet</label>
              <input
                data-testid="register-name"
                type="text"
                autoComplete="name"
                required
                minLength={2}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none transition-colors"
              />
            </div>
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Email</label>
              <input
                data-testid="register-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none transition-colors"
              />
            </div>
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Mot de passe (8 caractères min.)</label>
              <input
                data-testid="register-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none transition-colors"
              />
            </div>
            <div>
              <label className="label-caps text-[10px] text-[color:var(--gold)]/80 tracking-[0.25em] block mb-2">
                Code de parrainage (optionnel) · −10% en cadeau
              </label>
              <input
                data-testid="register-referral"
                type="text"
                placeholder="DOUBA-XXXXXXXX"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="w-full bg-transparent border-b border-[color:var(--gold)]/30 focus:border-[color:var(--gold)] py-3 outline-none transition-colors font-mono text-sm tracking-[0.15em]"
              />
            </div>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[color:var(--gold)] text-black py-4 label-caps text-xs tracking-[0.3em] flex items-center justify-center gap-2 hover:bg-white transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <>Créer mon compte <ArrowRight size={14} /></>}
            </button>
          </form>

          <p className="text-white/60 text-sm text-center mt-10">
            Déjà un compte ?{" "}
            <Link to="/connexion" data-testid="goto-login" className="text-[color:var(--gold)] hover:underline">Se connecter</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
